// Small shared helper the "Add to Home Screen" prompt watches. Any code that
// wants the prompt to become eligible on the current user's mobile device
// calls `notifyInstallMilestone()` after the user completes a useful action
// (e.g. Create Team, Add Player, Accept Invite, View a Fee).
//
// We deliberately keep this stateless on the caller side — it just flips
// two localStorage keys the InstallPrompt component reads.
//
// Keys:
//   tt_install_milestone_at   → epoch-ms of first-ever useful action
//   tt_install_dismissed_at   → epoch-ms of last dismissal (for 14-day cooloff)
//   tt_dashboard_visits       → integer count of coach/parent Home visits
//   tt_install_shown_session  → sessionStorage flag, per-tab
// Note: tt_install_shown_session lives in sessionStorage on purpose so it
// auto-clears when the tab closes.

const MILESTONE_KEY = "tt_install_milestone_at";
export const DISMISSED_KEY = "tt_install_dismissed_at";
const VISIT_COUNT_KEY = "tt_dashboard_visits";
export const SESSION_SHOWN_KEY = "tt_install_shown_session";
// Small custom event so the prompt component can react without a full reload.
export const MILESTONE_EVENT = "sf:install-milestone";

// Second-visit fallback threshold. On the Nth (or later) logged-in dashboard
// visit, if a milestone hasn't fired yet, we treat the return visit itself
// as a milestone. Kept as a constant so tests can reason about it.
export const VISIT_FALLBACK_AT = 2;

export function notifyInstallMilestone() {
  try {
    if (typeof window === "undefined") return;
    // Only the FIRST milestone matters — no need to overwrite once set.
    if (!window.localStorage.getItem(MILESTONE_KEY)) {
      window.localStorage.setItem(MILESTONE_KEY, String(Date.now()));
    }
    // Notify any mounted InstallPrompt in the same tab. Other tabs pick it
    // up via the built-in `storage` event.
    window.dispatchEvent(new CustomEvent(MILESTONE_EVENT));
  } catch { /* localStorage might be blocked (private mode) — silent */ }
}

export function hasInstallMilestone() {
  try {
    if (typeof window === "undefined") return false;
    return !!window.localStorage.getItem(MILESTONE_KEY);
  } catch { return false; }
}

// In-memory guard so React StrictMode's double-invoke and rapid re-mounts
// (e.g. bouncing between /dashboard and /teams during load) don't cause
// the counter to skip past VISIT_FALLBACK_AT prematurely. Reset when the
// tab closes since it lives on the module (SPA), which is exactly what
// we want — a fresh tab is a fresh session.
let _lastRecordedAt = 0;
const RECORD_DEBOUNCE_MS = 2000;

/**
 * Fallback trigger — call once whenever a coach or parent lands on their
 * Home dashboard. Increments the visit counter and, when it hits the
 * VISIT_FALLBACK_AT threshold, promotes the visit itself into a milestone
 * so returning-but-inactive users still see the install prompt.
 *
 * Never overwrites an existing milestone — once fired (via any path), the
 * prompt's own dismissal + 14-day rules take over.
 *
 * Debounced so React StrictMode's double-invoke (dev only) and back-to-
 * back mount cycles register as a single visit.
 */
export function recordDashboardVisit() {
  try {
    if (typeof window === "undefined") return;
    const now = Date.now();
    if (now - _lastRecordedAt < RECORD_DEBOUNCE_MS) return;
    _lastRecordedAt = now;
    const prev = parseInt(window.localStorage.getItem(VISIT_COUNT_KEY) || "0", 10) || 0;
    const next = prev + 1;
    window.localStorage.setItem(VISIT_COUNT_KEY, String(next));
    if (next >= VISIT_FALLBACK_AT && !window.localStorage.getItem(MILESTONE_KEY)) {
      notifyInstallMilestone();
    }
  } catch { /* ignore */ }
}
